<template>
  <div class="wgt-article-masonry">
    <div class="wgt-article-masonry__col">
      <div
        v-for="entry in columnLeft"
        :key="cardKey(entry.item, entry.idx)"
        class="article-card"
        :data-source-idx="entry.idx"
      >
        <div class="article-card__cover">
          <video
            v-if="showVideoCover(entry.item)"
            class="article-card__img article-card__video"
            :src="videoMaterialUrl(entry.item)"
            :poster="videoPosterUrl(entry.item)"
            muted
            playsinline
            preload="metadata"
            @loadedmetadata="(e) => onCardImgLoad(entry.item, entry.idx, e)"
            @error="onImgError(entry.item, entry.idx, $event)"
          />
          <img
            v-else
            class="article-card__img"
            :src="coverUrl(entry.item)"
            alt=""
            @load="(e) => onCardImgLoad(entry.item, entry.idx, e)"
            @error="onImgError(entry.item, entry.idx, $event)"
          />
          <div v-if="isVideoArticle(entry.item)" class="article-card__play">
            <div class="article-card__play-mask" />
            <i class="el-icon-video-play article-card__play-icon" />
          </div>
        </div>
        <p class="article-card__title">
          {{ entry.item.title }}
        </p>
      </div>
    </div>
    <div class="wgt-article-masonry__col">
      <div
        v-for="entry in columnRight"
        :key="cardKey(entry.item, entry.idx)"
        class="article-card"
        :data-source-idx="entry.idx"
      >
        <div class="article-card__cover">
          <video
            v-if="showVideoCover(entry.item)"
            class="article-card__img article-card__video"
            :src="videoMaterialUrl(entry.item)"
            :poster="videoPosterUrl(entry.item)"
            muted
            playsinline
            preload="metadata"
            @loadedmetadata="(e) => onCardImgLoad(entry.item, entry.idx, e)"
            @error="onImgError(entry.item, entry.idx, $event)"
          />
          <img
            v-else
            class="article-card__img"
            :src="coverUrl(entry.item)"
            alt=""
            @load="(e) => onCardImgLoad(entry.item, entry.idx, e)"
            @error="onImgError(entry.item, entry.idx, $event)"
          />
          <div v-if="isVideoArticle(entry.item)" class="article-card__play">
            <div class="article-card__play-mask" />
            <i class="el-icon-video-play article-card__play-icon" />
          </div>
        </div>
        <p class="article-card__title">
          {{ entry.item.title }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
const DEFAULT_CARD_HEIGHT = 168

export default {
  name: 'ArticleWaterfall',
  props: {
    list: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      columnLeft: [],
      columnRight: [],
      cardHeights: {},
      _syncScheduled: false
    }
  },
  computed: {
    renderList() {
      return Array.isArray(this.list) ? this.list : []
    }
  },
  watch: {
    list: {
      handler() {
        this.cardHeights = {}
        this.$nextTick(() => {
          this.syncColumns()
        })
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    window.addEventListener('resize', this.onWindowResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize)
  },
  methods: {
    cardKey(item, listIndex) {
      if (item && item.id != null) return String(item.id)
      return 'article-' + listIndex
    },
    rowHeight(item, idx) {
      const key = this.cardKey(item, idx)
      const h = this.cardHeights[key]
      return typeof h === 'number' && h > 0 ? h : DEFAULT_CARD_HEIGHT
    },
    syncColumns() {
      const left = []
      const right = []
      let sumL = 0
      let sumR = 0
      this.renderList.forEach((item, idx) => {
        const h = this.rowHeight(item, idx)
        if (sumL <= sumR) {
          left.push({ item, idx })
          sumL += h
        } else {
          right.push({ item, idx })
          sumR += h
        }
      })
      this.columnLeft = left
      this.columnRight = right
    },
    scheduleSyncColumns() {
      if (this._syncScheduled) return
      this._syncScheduled = true
      requestAnimationFrame(() => {
        this._syncScheduled = false
        this.syncColumns()
      })
    },
    onCardImgLoad(item, idx, e) {
      const el = e.target && e.target.closest('.article-card')
      if (!el) return
      const h = el.offsetHeight
      const key = this.cardKey(item, idx)
      if (h > 0 && this.cardHeights[key] !== h) {
        this.$set(this.cardHeights, key, h)
        this.scheduleSyncColumns()
      }
    },
    onImgError(item, idx, e) {
      if (e.target) e.target.style.visibility = 'hidden'
      this.$nextTick(() => {
        const el = e.target && e.target.closest('.article-card')
        if (!el) return
        const h = el.offsetHeight
        const key = this.cardKey(item, idx)
        if (h > 0) {
          this.$set(this.cardHeights, key, h)
          this.scheduleSyncColumns()
        }
      })
    },
    coverUrl(item) {
      if (!item) return ''
      return item.image_url || ''
    },
    parseArticleContentField(raw) {
      if (raw == null) return []
      let c = raw
      if (typeof c === 'string') {
        const s = c.trim()
        if (!s) return []
        try {
          c = JSON.parse(s)
        } catch {
          return []
        }
      }
      return Array.isArray(c) ? c : []
    },
    parseArticleContent(item) {
      if (!item) return []
      const fromContent = this.parseArticleContentField(item.content)
      if (fromContent.length) return fromContent
      return this.parseArticleContentField(item.materials)
    },
    videoMaterialUrl(item) {
      if (!this.isVideoArticle(item)) return ''
      const arr = this.parseArticleContent(item)
      const first = arr[0]
      const url = first && first.material_url
      return url ? String(url) : ''
    },
    showVideoCover(item) {
      return !!this.videoMaterialUrl(item)
    },
    videoPosterUrl(item) {
      const v = this.videoMaterialUrl(item)
      const img = item && item.image_url ? String(item.image_url).trim() : ''
      if (!img || !v || img === v) return undefined
      return img
    },
    isVideoArticle(item) {
      if (!item || typeof item !== 'object') return false
      const t = String(item.article_type || '').toLowerCase()
      return t === 'video' || t === '2'
    },
    onWindowResize() {
      this.$nextTick(() => {
        if (!this.$el) return
        const cards = this.$el.querySelectorAll('.article-card[data-source-idx]')
        cards.forEach((card) => {
          const idx = parseInt(card.getAttribute('data-source-idx'), 10)
          if (Number.isNaN(idx)) return
          const item = this.renderList[idx]
          if (item && card.offsetHeight > 0) {
            this.$set(this.cardHeights, this.cardKey(item, idx), card.offsetHeight)
          }
        })
        this.syncColumns()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.wgt-article-masonry {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
}

.wgt-article-masonry__col {
  flex: 1;
  min-width: 0;
}

.article-card {
  break-inside: avoid;
  margin-bottom: 10px;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
}

.article-card__cover {
  position: relative;
  width: 100%;
  background: #f5f7fa;
}

.article-card__img {
  width: 100%;
  height: auto;
  display: block;
}

.article-card__video {
  width: 100%;
  height: 225px;
  display: block;
  object-fit: cover;
}

.article-card__play {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.article-card__play-mask {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.22);
}

.article-card__play-icon {
  position: relative;
  z-index: 2;
  font-size: 42px;
  color: #fff;
}

.article-card__title {
  margin: 0;
  padding: 10px 10px 12px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  color: #1d1d1f;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
</style>
